import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const PartnerAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PartnerAuthProvider = ({ children }) => {
  const [partner, setPartner] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('partner_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const storedPartner = localStorage.getItem('partner_data');
      if (storedPartner) {
        setPartner(JSON.parse(storedPartner));
      }
      
      // Verify by fetching profile
      const response = await axios.get(`${API}/partner-portal/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPartner(response.data);
      localStorage.setItem('partner_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshPartner = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/partner-portal/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartner(response.data);
      localStorage.setItem('partner_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to refresh partner data:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/partner/login`, { email, password });
      const { access_token, partner: partnerData } = response.data;
      
      localStorage.setItem('partner_token', access_token);
      localStorage.setItem('partner_data', JSON.stringify(partnerData));
      setToken(access_token);
      setPartner(partnerData);
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('partner_token');
    localStorage.removeItem('partner_data');
    setToken(null);
    setPartner(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <PartnerAuthContext.Provider value={{ partner, token, loading, login, logout, getAuthHeader, refreshPartner }}>
      {children}
    </PartnerAuthContext.Provider>
  );
};

export const usePartnerAuth = () => {
  const context = useContext(PartnerAuthContext);
  if (!context) {
    throw new Error('usePartnerAuth must be used within PartnerAuthProvider');
  }
  return context;
};
